import { useEffect, useState } from 'react';
import {
  LayoutDashboard, Loader2, AlertTriangle, Clock, PauseCircle, UserX,
  Printer, Info, Wrench, Repeat, Users, Gauge, Timer,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import { useEstiloDeImpresion } from '../experiencia/estiloDeImpresion';

/* =====================================================================
   Tablero del encargado · DIRECTRIZ-TICKETS F5
   =====================================================================
   Lo que ve quien lleva la mesa de un vistazo: abiertos por prioridad,
   vencidos, tiempo medio de primera respuesta y de resolución, % dentro
   de SLA, activos con más fallas, problemas que se repiten y carga por
   técnico. Cada número lleva su FICHA («Cómo se calcula»): qué cuenta,
   qué excluye y cómo se pausa el reloj. Se exporta a PDF para juntas.

   Nada de prueba o ejemplo entra en un número (lo hace el servidor); si
   la empresa entera es de ejemplo, el tablero lo DICE en vez de fingir.
   ===================================================================== */

const PRIORIDAD = {
  1: { texto: 'Crítica', clase: 'bg-red-100 text-red-800' },
  2: { texto: 'Alta', clase: 'bg-orange-100 text-orange-800' },
  3: { texto: 'Media', clase: 'bg-yellow-100 text-yellow-800' },
  4: { texto: 'Baja', clase: 'bg-gray-100 text-gray-700' },
};

const VENTANAS = [
  { dias: 7, etiqueta: '7 días' },
  { dias: 30, etiqueta: '30 días' },
  { dias: 90, etiqueta: '90 días' },
];

function humanoMin(min) {
  if (min == null) return '—';
  if (min === 0) return '0 min';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h >= 24) { const d = Math.floor(h / 24); return `${d} d ${h % 24} h`; }
  if (h > 0) return m ? `${h} h ${m} min` : `${h} h`;
  return `${m} min`;
}

// El color de un porcentaje de SLA: verde bien, amarillo atención, rojo
// problema. Los mismos colores de estado de todo el sistema.
function colorPct(pct) {
  if (pct == null) return 'text-gray-400';
  if (pct >= 90) return 'text-green-700';
  if (pct >= 70) return 'text-amber-600';
  return 'text-red-700';
}

// La ficha de una métrica. Se abre al tocar «Cómo se calcula» y muestra
// la nota de capa 3 del catálogo (fórmula, exclusiones, versión). No se
// imprime: en el PDF los números van limpios.
function Ficha({ id }) {
  const [abierta, setAbierta] = useState(false);
  return (
    <span className="sin-imprimir">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" /> Cómo se calcula
      </button>
      {abierta && (
        <span className="mt-1 block rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
          <Ayuda id={id} />
        </span>
      )}
    </span>
  );
}

// Una tarjeta de número grande con su ficha.
function Tarjeta({ icono: Icono, titulo, valor, sub, tono = 'text-gray-900', fichaId }) {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
        {Icono && <Icono className="h-4 w-4" aria-hidden="true" />} {titulo}
      </div>
      <div className={`mt-1 text-3xl font-bold ${tono}`}>{valor}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
      {fichaId && <Ficha id={fichaId} />}
    </div>
  );
}

export default function TableroEncargado() {
  const [dias, setDias] = useState(30);
  const [t, setT] = useState(null);
  const [error, setError] = useState('');

  useEstiloDeImpresion(`
    @media print {
      @page { margin: 12mm; }
      body * { visibility: hidden; }
      #tablero-imprimible, #tablero-imprimible * { visibility: visible; }
      #tablero-imprimible { position: absolute; left: 0; top: 0; width: 100%; }
      .sin-imprimir { display: none !important; }
    }
  `);

  function recargar() {
    api.get(`/tickets/tablero?dias=${dias}`)
      .then((d) => { setT(d); setError(''); })
      .catch((e) => setError(
        e instanceof ErrorApi ? e.message
          : 'No se pudo cargar el tablero. Revisa tu conexión y vuelve a intentar con el botón de abajo.',
      ));
  }
  useEffect(recargar, [dias]);

  const vacio = t && !t.empresa_es_ejemplo && t.total_abiertos === 0
    && t.muestra_promedios === 0 && t.activos_con_mas_fallas.length === 0
    && t.problemas_que_se_repiten.length === 0 && t.carga_por_tecnico.length === 0;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
      <header className="sin-imprimir">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <LayoutDashboard className="h-6 w-6" aria-hidden="true" /> Tablero del encargado
        </h1>
        <p className="text-gray-600">Cómo va la mesa de un vistazo, para atender lo urgente y ver dónde apretar.</p>
        <Ayuda id="tablero.pantalla" />
      </header>

      <div className="flex flex-wrap items-center justify-between gap-2 sin-imprimir">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Ventana de tiempo">
          {VENTANAS.map((v) => (
            <button
              key={v.dias}
              type="button"
              aria-pressed={dias === v.dias}
              onClick={() => { setT(null); setDias(v.dias); }}
              className={`min-h-[44px] rounded-full px-4 text-sm font-semibold ${
                dias === v.dias
                  ? 'bg-[var(--color-marca)] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {v.etiqueta}
            </button>
          ))}
        </div>
        <div className="flex flex-col items-end">
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!t}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            <Printer className="h-5 w-5" aria-hidden="true" /> Exportar a PDF
          </button>
          <Ayuda id="tablero.exportar" />
        </div>
      </div>

      {error && (
        <div role="alert" className="flex flex-col gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800 sin-imprimir">
          <span>{error}</span>
          <button type="button" onClick={() => { setT(null); setError(''); recargar(); }}
                  className="self-start rounded-lg border border-red-300 bg-white px-3 py-1 font-semibold text-red-800">
            Volver a intentar
          </button>
        </div>
      )}

      {!t && !error && (
        <p className="flex items-center gap-2 py-10 text-sm text-gray-500 sin-imprimir">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando el tablero…
        </p>
      )}

      {t && t.empresa_es_ejemplo && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Esta es una empresa de ejemplo: sus datos son de demostración y no se cuentan en el tablero.
          Cuando trabajes con tu empresa real, aquí verás sus números.
        </div>
      )}

      {vacio && (
        <EstadoVacio
          icono={LayoutDashboard}
          titulo="Todavía no hay tickets que medir"
          descripcion="Cuando se reporten y atiendan problemas, aquí verás cuántos hay abiertos, cuánto se tarda en responderlos y qué falla más. Empieza en «Mesa de ayuda»."
        />
      )}

      {t && !t.empresa_es_ejemplo && !vacio && (
        <div id="tablero-imprimible" className="flex flex-col gap-4">
          <p className="hidden text-lg font-semibold text-gray-900 print:block">
            Tablero de la mesa de ayuda · últimos {t.dias} días
          </p>

          {/* Estado actual */}
          <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Tarjeta icono={LayoutDashboard} titulo="Abiertos" valor={t.total_abiertos}
                     sub="Ni resueltos ni cerrados" fichaId="tablero.ficha_abiertos" />
            <Tarjeta icono={AlertTriangle} titulo="Vencidos" valor={t.vencidos}
                     tono={t.vencidos > 0 ? 'text-red-700' : 'text-green-700'}
                     sub="Pasaron su plazo de resolución" fichaId="tablero.ficha_vencidos" />
            <Tarjeta icono={PauseCircle} titulo="En espera" valor={t.en_espera}
                     sub="Con el reloj detenido" />
            <Tarjeta icono={UserX} titulo="Sin asignar" valor={t.sin_asignar}
                     tono={t.sin_asignar > 0 ? 'text-amber-600' : 'text-green-700'}
                     sub="Nadie los ha tomado" />
          </section>

          {/* Abiertos por prioridad */}
          <section className="rounded-2xl border border-gray-200 bg-white p-4">
            <h2 className="flex items-center gap-2 font-medium text-gray-900">
              <LayoutDashboard className="h-4 w-4" aria-hidden="true" /> Abiertos por prioridad
            </h2>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {t.abiertos_por_prioridad.map((p) => (
                <div key={p.prioridad} className="flex flex-col items-center rounded-xl bg-gray-50 p-2">
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${PRIORIDAD[p.prioridad].clase}`}>
                    {PRIORIDAD[p.prioridad].texto}
                  </span>
                  <span className="mt-1 text-2xl font-bold text-gray-900">{p.n}</span>
                </div>
              ))}
            </div>
            <Ficha id="tablero.ficha_abiertos" />
          </section>

          {/* Tiempos y SLA */}
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Tarjeta icono={Timer} titulo="Primera respuesta (media)"
                     valor={humanoMin(t.tiempo_medio_primera_respuesta_min)}
                     sub={`En horario laboral · últimos ${t.dias} días`}
                     fichaId="tablero.ficha_primera_respuesta" />
            <Tarjeta icono={Clock} titulo="Resolución (media)"
                     valor={humanoMin(t.tiempo_medio_resolucion_min)}
                     sub={`${t.muestra_promedios} resueltos · últimos ${t.dias} días`}
                     fichaId="tablero.ficha_resolucion" />
            <Tarjeta icono={Gauge} titulo="Dentro de SLA (resolución)"
                     valor={t.pct_dentro_sla == null ? '—' : `${t.pct_dentro_sla}%`}
                     tono={colorPct(t.pct_dentro_sla)}
                     sub={`${t.muestra_sla} con plazo`} fichaId="tablero.ficha_sla" />
            <Tarjeta icono={Gauge} titulo="Dentro de SLA (1.ª respuesta)"
                     valor={t.pct_primera_respuesta_en_sla == null ? '—' : `${t.pct_primera_respuesta_en_sla}%`}
                     tono={colorPct(t.pct_primera_respuesta_en_sla)}
                     sub={`${t.muestra_sla_pr} con plazo`} fichaId="tablero.ficha_sla" />
          </section>

          {/* Listas */}
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Lista icono={Wrench} titulo="Activos con más fallas"
                   filas={t.activos_con_mas_fallas}
                   render={(a) => ({ etiqueta: `${a.activo_tipo || 'Activo'} #${a.activo_ref}`, n: a.n })}
                   vacio="Ningún activo con fallas en este periodo."
                   fichaId="tablero.ficha_activos" />
            <Lista icono={Repeat} titulo="Problemas que se repiten"
                   filas={t.problemas_que_se_repiten}
                   render={(p) => ({ etiqueta: p.categoria, n: p.n })}
                   vacio="Sin reportes en este periodo."
                   fichaId="tablero.ficha_problemas" />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4">
            <h2 className="flex items-center gap-2 font-medium text-gray-900">
              <Users className="h-4 w-4" aria-hidden="true" /> Carga por técnico
            </h2>
            {t.carga_por_tecnico.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">Ningún ticket asignado ahora mismo.</p>
            ) : (
              <ul className="mt-2 flex flex-col gap-1">
                {t.carga_por_tecnico.map((c) => (
                  <li key={c.id_usuario} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate text-gray-800">{c.nombre_completo}</span>
                    <span className="font-semibold text-gray-900">{c.abiertos} abiertos</span>
                  </li>
                ))}
              </ul>
            )}
            <Ficha id="tablero.ficha_carga" />
          </section>
        </div>
      )}
    </div>
  );
}

function Lista({ icono: Icono, titulo, filas, render, vacio, fichaId }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <h2 className="flex items-center gap-2 font-medium text-gray-900">
        {Icono && <Icono className="h-4 w-4" aria-hidden="true" />} {titulo}
      </h2>
      {filas.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{vacio}</p>
      ) : (
        <ol className="mt-2 flex flex-col gap-1">
          {filas.map((f, i) => {
            const { etiqueta, n } = render(f);
            return (
              <li key={i} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
                <span className="min-w-0 truncate text-gray-800">{etiqueta}</span>
                <span className="font-semibold text-gray-900">{n}</span>
              </li>
            );
          })}
        </ol>
      )}
      {fichaId && <Ficha id={fichaId} />}
    </div>
  );
}
